import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import PaymentRecorder from "@/components/PaymentRecorder";
import { useAuth } from "@/context/AuthContext";
import { Wallet, CheckCircle2, Clock, Users2 } from "lucide-react";

function Sum({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card-surface card-tint rounded-2xl p-4" style={{ "--tint": accent }} data-testid={testid}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center mb-2" style={{ background: `${accent}22`, color: accent }}><Icon size={18} /></div>
      <div className="font-bold font-heading tracking-tight whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(0.7rem, 2.5vw, 0.88rem)", fontVariantNumeric: "tabular-nums" }} title={typeof value === "string" ? value : undefined}>{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

const STATUS_TABS = [["belum", "Belum Lunas"], ["lunas", "Sudah Lunas"], ["all", "Semua"]];

// Design and Produksi are two separate revenue ledgers — instead of one wide table
// cramming both side by side, this page shows one at a time via a top-level toggle,
// each with its own simple 3-tab (Belum/Lunas/Semua) view, matching how Rekap Omset
// and Dashboard already split the two.
export default function Payments() {
  const { user } = useAuth();
  const readOnly = false;
  const [selected, setSelected] = useState(null);
  const [kind, setKind] = useState("design"); // "design" | "produksi" | "gabungan"
  const [tab, setTab] = useState("belum");
  const { data, isLoading } = useQuery({ queryKey: ["receivables"], queryFn: async () => (await api.get("/receivables")).data });
  const s = data?.summary;
  const all = data?.items || [];
  const isProd = kind === "produksi";
  const isGab = kind === "gabungan";

  // /receivables already returns the union (Design closed OR Produksi active), so the
  // combined view just re-uses `all` as-is — no separate query needed.
  const active = isGab ? all : all.filter((l) => isProd ? !!l.produksi_closing_date : l.status === "Closing");
  const sisaOf = (l) => isGab ? l.sisa_piutang_gabungan : isProd ? l.produksi_sisa_piutang : l.sisa_piutang;
  const items = active.filter((l) => tab === "all" ? true : tab === "lunas" ? sisaOf(l) <= 0 : sisaOf(l) > 0);
  const gabLunasCount = all.filter((l) => l.sisa_piutang_gabungan <= 0).length;
  const lunasCount = isGab ? gabLunasCount : isProd ? s?.produksi_lunas ?? 0 : s?.lunas ?? 0;
  const belumCount = isGab ? all.length - gabLunasCount : isProd ? s?.produksi_belum_lunas ?? 0 : s?.belum_lunas ?? 0;
  const totalCount = active.length;

  return (
    <div className="px-4 py-5 max-w-5xl mx-auto print-area md:max-w-none md:px-8">
      <h1 className="text-xl font-bold font-heading tracking-tight mb-1">Piutang & Pembayaran</h1>
      <p className="text-sm text-zinc-500 mb-4">Catat pembayaran masuk (dengan bukti transfer), pantau termin & piutang sampai lunas.</p>

      {isLoading || !data ? null : (
        <>
          <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-4 w-fit">
            <button data-testid="piutang-kind-design" onClick={() => { setKind("design"); setTab("belum"); }} className={`px-4 py-1.5 rounded-md text-sm font-medium ${kind === "design" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Design</button>
            <button data-testid="piutang-kind-produksi" onClick={() => { setKind("produksi"); setTab("belum"); }} className={`px-4 py-1.5 rounded-md text-sm font-medium ${isProd ? "bg-purple-500/20 text-purple-300" : "text-zinc-500"}`}>Produksi</button>
            <button data-testid="piutang-kind-gabungan" onClick={() => { setKind("gabungan"); setTab("belum"); }} className={`px-4 py-1.5 rounded-md text-sm font-medium ${isGab ? "bg-amber-500/20 text-amber-300" : "text-zinc-500"}`}>Gabungan</button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Sum icon={Users2} label={`Total Closing (${isGab ? "Gabungan" : isProd ? "Produksi" : "Design"})`} value={totalCount} accent={isGab ? "#EAB308" : isProd ? "#8B5CF6" : "#3B82F6"} testid="sum-closing" />
            <Sum icon={CheckCircle2} label="Sudah Lunas" value={lunasCount} accent="#10B981" testid="sum-lunas" />
            <Sum icon={Clock} label="Masih Ada Piutang" value={belumCount} accent="#F59E0B" testid="sum-belum" />
            <Sum icon={Wallet} label="Total Sisa Piutang" value={rupiah(isGab ? s.total_piutang_gabungan : isProd ? s.total_piutang_produksi : s.total_piutang)} accent="#EF4444" testid="sum-piutang" />
          </div>

          <div className="flex gap-2 mb-3 flex-wrap">
            {STATUS_TABS.map(([v, l]) => (
              <button key={v} data-testid={`piutang-tab-${v}`} onClick={() => setTab(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${tab === v ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>
                {l} ({v === "lunas" ? lunasCount : v === "belum" ? belumCount : totalCount})
              </button>
            ))}
          </div>

          {/* Mobile: a stacked card per row instead of forcing the same wide table into a
              350px-wide screen — that used to rely entirely on horizontal scroll, and a
              screenshot mid-scroll (or a device that doesn't scroll it at all) showed
              columns squashed together with no header in view, reading as "berantakan".
              Desktop keeps the table (plenty of width there, no reason to change it). */}
          <div className="md:hidden space-y-2.5">
            {items.map((l, i) => {
              const sisa = sisaOf(l);
              const danger = l.days_since_closing > 30 && sisa > 0;
              return (
                <div key={l.lead_id} data-testid={`piutang-card-${l.lead_id}`} onClick={() => setSelected(l.lead_id)}
                  className="card-surface rounded-2xl p-3.5 cursor-pointer active:bg-white/[0.03]">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{l.nama}</div>
                      <div className="text-xs text-zinc-500 truncate">{l.sales_name}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {sisa <= 0 ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Lunas</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Belum</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${danger ? "bg-red-500/15 text-red-400" : "bg-white/5 text-zinc-500"}`}>{l.days_since_closing}h</span>
                    </div>
                  </div>
                  {isGab ? (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs pt-2 border-t border-white/5">
                      <div><span className="text-zinc-500">Nilai Design </span><span className="tabular-nums">{l.status === "Closing" ? rupiah(l.nilai_deal) : "-"}</span></div>
                      <div><span className="text-zinc-500">Nilai Produksi </span><span className="text-purple-300 tabular-nums">{l.produksi_closing_date ? rupiah(l.nilai_produksi) : "-"}</span></div>
                      <div><span className="text-zinc-500">Total Nilai </span><span className="font-medium tabular-nums">{rupiah(l.nilai_total_gabungan)}</span></div>
                      <div><span className="text-zinc-500">Sisa </span><span className="font-semibold text-amber-400 tabular-nums">{rupiah(sisa)}</span></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs pt-2 border-t border-white/5">
                      <div className="col-span-2"><span className="text-zinc-500">{isProd ? "Tgl Mulai Produksi " : "Tgl Closing "}</span><span>{fmtDate(isProd ? l.produksi_closing_date : l.closing_date)}</span></div>
                      <div><span className="text-zinc-500">Nilai </span><span className="tabular-nums">{rupiah(isProd ? l.nilai_produksi : l.nilai_deal)}</span></div>
                      <div><span className="text-zinc-500">Dibayar </span><span className="text-emerald-400 tabular-nums">{rupiah(isProd ? l.produksi_paid_total : l.paid_total)}</span></div>
                      <div className="col-span-2"><span className="text-zinc-500">Sisa </span><span className="font-semibold text-amber-400 tabular-nums">{rupiah(sisa)}</span></div>
                    </div>
                  )}
                </div>
              );
            })}
            {items.length === 0 && (
              <div className="card-surface rounded-2xl text-center text-zinc-600 py-10 text-sm">
                {tab === "lunas" ? "Belum ada yang lunas" : tab === "belum" ? "Semua piutang lunas 🎉" : `Belum ada data ${isProd ? "produksi" : isGab ? "closing/produksi" : "closing"}`}
              </div>
            )}
          </div>

          <div className="hidden md:block card-surface rounded-2xl overflow-x-auto hide-scrollbar">
            <table className={`w-full text-sm ${isGab ? "min-w-[920px]" : "min-w-[720px]"}`}>
              <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/5">
                <th className="text-left py-2.5 px-3 font-medium">#</th>
                <th className="text-left font-medium">Nama Customer</th>
                <th className="text-left font-medium">Admin/Sales</th>
                {isGab ? (
                  <>
                    <th className="text-right font-medium">Nilai Design</th>
                    <th className="text-right font-medium">Nilai Produksi</th>
                    <th className="text-right font-medium">Total Nilai</th>
                    <th className="text-right font-medium">Sisa Gabungan</th>
                  </>
                ) : (
                  <>
                    <th className="text-left font-medium">{isProd ? "Tgl Mulai Produksi" : "Tgl Closing"}</th>
                    <th className="text-right font-medium">Nilai</th>
                    <th className="text-right font-medium">Dibayar</th>
                    <th className="text-right font-medium">Sisa</th>
                  </>
                )}
                <th className="text-center font-medium">Status</th>
                <th className="text-right font-medium px-3">Umur</th>
              </tr></thead>
              <tbody>
                {items.map((l, i) => {
                  const sisa = sisaOf(l);
                  const danger = l.days_since_closing > 30 && sisa > 0;
                  return (
                    <tr key={l.lead_id} data-testid={`piutang-row-${l.lead_id}`} onClick={() => setSelected(l.lead_id)} className="border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.02]">
                      <td className="py-2.5 px-3 text-zinc-500">{i + 1}</td>
                      <td className="font-medium">{l.nama}</td>
                      <td className="text-zinc-400">{l.sales_name}</td>
                      {isGab ? (
                        <>
                          <td className="text-right whitespace-nowrap tabular-nums">{l.status === "Closing" ? rupiah(l.nilai_deal) : "-"}</td>
                          <td className="text-right whitespace-nowrap tabular-nums text-purple-300">{l.produksi_closing_date ? rupiah(l.nilai_produksi) : "-"}</td>
                          <td className="text-right whitespace-nowrap tabular-nums font-medium">{rupiah(l.nilai_total_gabungan)}</td>
                          <td className="text-right font-semibold text-amber-400 whitespace-nowrap tabular-nums">{rupiah(sisa)}</td>
                        </>
                      ) : (
                        <>
                          <td className="text-zinc-400 whitespace-nowrap">{fmtDate(isProd ? l.produksi_closing_date : l.closing_date)}</td>
                          <td className="text-right whitespace-nowrap tabular-nums">{rupiah(isProd ? l.nilai_produksi : l.nilai_deal)}</td>
                          <td className="text-right text-emerald-400 whitespace-nowrap tabular-nums">{rupiah(isProd ? l.produksi_paid_total : l.paid_total)}</td>
                          <td className="text-right font-semibold text-amber-400 whitespace-nowrap tabular-nums">{rupiah(sisa)}</td>
                        </>
                      )}
                      <td className="text-center">{sisa <= 0 ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Lunas</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Belum</span>}</td>
                      <td className="text-right px-3"><span className={`px-1.5 py-0.5 rounded text-xs ${danger ? "bg-red-500/15 text-red-400" : "bg-white/5 text-zinc-400"}`}>{l.days_since_closing}h</span></td>
                    </tr>
                  );
                })}
                {items.length === 0 && <tr><td colSpan={isGab ? 9 : 9} className="text-center text-zinc-600 py-10">{tab === "lunas" ? "Belum ada yang lunas" : tab === "belum" ? "Semua piutang lunas 🎉" : `Belum ada data ${isProd ? "produksi" : isGab ? "closing/produksi" : "closing"}`}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      <PaymentRecorder leadId={selected} readOnly={readOnly} initialKind={kind} onClose={() => setSelected(null)} />
    </div>
  );
}
